import type de from './de';

/**
 * UI texts of the web client (French). Common terms follow the legacy dictionaries
 * (lang/internal/fr.json: « Enregistrement », « Paramètres », « Déconnexion », « Jeu de couleurs » …).
 */
const fr: typeof de = {
  app: {
    name: 'Creator',
    loading: 'Chargement …',
    retry: 'Réessayer',
    close: 'Fermer',
    cancel: 'Annuler',
    ok: 'OK',
    save: 'Enregistrer',
    back: 'Retour',
    menu: 'Menu',
    offline: 'Pas de connexion – les modifications apparaîtront après la reconnexion.',
    updateAvailable: 'Une nouvelle version est disponible.',
    updateReload: 'Recharger',
    skipToContent: 'Aller au contenu',
  },
  nav: {
    home: 'Accueil',
    navigation: 'Navigation',
    database: 'Base de données',
    filter: 'Filtrer les tables',
    noMatch: 'Aucune table trouvée',
    favorites: 'Favoris',
    recent: 'Utilisées récemment',
    other: 'Autres',
    pin: 'Épingler la barre latérale',
    unpin: 'Masquer la barre latérale',
    toggleSidebar: 'Afficher/masquer la barre latérale',
    breadcrumb: 'Fil d’Ariane',
    addFavorite: 'Ajouter aux favoris',
    removeFavorite: 'Retirer des favoris',
  },
  user: {
    printLayouts: "Mises en page d'impression",
    menu: 'Menu utilisateur',
    language: 'Langue',
    colorScheme: 'Jeu de couleurs',
    colorTheme: 'Thème de couleurs',
    settings: 'Paramètres',
    password: 'Changer le mot de passe',
    twoFactor: 'Connexion à deux facteurs',
    twoFactorOn: 'active',
    switchUser: 'Changer d’utilisateur',
    switchUserPrompt: 'Sous quel utilisateur souhaitez-vous travailler ?',
    switchBack: 'Revenir à {user}',
    logout: 'Déconnexion',
    shortcuts: 'Raccourcis clavier',
    impersonating: 'Connecté en tant que {user} par {admin}.',
  },
  scheme: { auto: 'Automatique', light: 'Clair', dark: 'Sombre' },
  theme: {
    default: 'Par défaut',
    blue: 'Bleu',
    green: 'Vert',
    grey: 'Gris',
    orange: 'Orange',
    petrol: 'Pétrole',
    red: 'Rouge',
    sportsman: 'Sportsman',
    yellow: 'Jaune',
  },
  lang: { de: 'Deutsch', en: 'English', fr: 'Français', nl: 'Nederlands', auto: 'Langue du navigateur' },
  login: {
    title: 'Connexion',
    username: 'Nom d’utilisateur',
    password: 'Mot de passe',
    submit: 'Se connecter',
    totpTitle: 'Code de vérification',
    totpHint: 'Saisissez le code de votre application d’authentification ou un code de secours.',
    totp: 'Code',
    actAs: 'Démarrer en tant qu’utilisateur (administrateurs uniquement)',
    invalid: 'Nom d’utilisateur ou mot de passe incorrect.',
    totpInvalid: 'Le code n’est pas valide.',
    throttled: 'Trop de tentatives. Veuillez patienter {seconds} secondes.',
    throttledShort: 'Trop de tentatives. Veuillez réessayer plus tard.',
    unavailable: 'La connexion est actuellement indisponible.',
    expired: 'Votre session a expiré. Veuillez vous reconnecter.',
  },
  home: {
    title: 'Vue d’ensemble',
    databases: 'Bases de données',
    empty: 'Aucune table disponible.',
    tables: '{n} tables',
  },
  table: {
    rows: 'Enregistrements',
    placeholder: 'Vue provisoire – la vue tableau complète suivra.',
    empty: 'Aucun enregistrement.',
    new: 'Nouveau',
    refresh: 'Actualiser',
    of: 'sur',
    live: 'Mise à jour en direct active',
  },
  record: {
    title: 'Enregistrement {id}',
    new: 'Nouvel enregistrement',
    prev: 'Enregistrement précédent',
    next: 'Enregistrement suivant',
    readonly: 'lecture seule',
  },
  account: {
    title: 'Compte',
    settings: 'Paramètres',
    password: 'Mot de passe',
    security: 'Connexion à deux facteurs',
    saved: 'Enregistré.',
    localOnly: 'Les paramètres ne sont actuellement enregistrés que sur cet appareil.',
    sectionDisplay: 'Affichage',
    sectionTables: 'Tables et enregistrements',
    reset: 'Réinitialiser',
    resetDefault: 'Restaurer la valeur par défaut',
    showUrl: 'Ouvrir les enregistrements',
    showUrlOverlay: 'En superposition au-dessus de la table',
    showUrlTab: 'Dans un nouvel onglet',
    showUrlPage: 'Sur une page séparée',
    margin: 'Espacement des cellules',
    marginNone: 'Aucun',
    marginLow: 'Faible',
    marginMedium: 'Moyen',
    marginHigh: 'Grand',
    textSearch: 'Ligne de recherche dans les tables',
    textSearchNever: 'Ne jamais afficher',
    textSearchAlways: 'Toujours afficher',
    textSearchFilter: 'Afficher seulement si un filtre est actif',
    fixedHeader: 'En-tête du tableau fixe',
    sidebarPinned: 'Barre latérale épinglée (ordinateur)',
    pageSize: 'Enregistrements par page',
    pageSizeDefault: 'Valeur par défaut de la table',
    help: {
      lang: 'Langue de l’interface. « Langue du navigateur » suit le réglage de votre navigateur.',
      colorScheme: 'Clair, sombre ou automatique selon le réglage de l’appareil.',
      theme: 'Couleurs d’accentuation de l’en-tête, de la barre latérale et des boutons.',
      margin: 'Marge à l’intérieur des cellules – plus petite, elle affiche plus de lignes à la fois.',
      sidebarPinned: 'La barre latérale reste visible sur les grands écrans.',
      showUrlOverlay: 'Ouvre l’enregistrement dans une grande fenêtre au-dessus de la table (plein écran sur mobile) ; la table reste en place.',
      showUrlTab: 'Ouvre chaque enregistrement dans un nouvel onglet du navigateur.',
      showUrlPage: 'Remplace la table par l’enregistrement ; « Retour » ramène à la table.',
      textSearch: 'Ligne de champs de recherche sous l’en-tête du tableau. « Seulement si un filtre est actif » l’affiche dès qu’un filtre est défini.',
      fixedHeader: 'L’en-tête du tableau reste visible pendant le défilement.',
      pageSize: 'Nombre d’enregistrements qu’une table charge par page.',
      shortcuts: 'Cliquez sur +, puis appuyez sur la combinaison de touches souhaitée (Échap annule). Vos affectations sont enregistrées dans votre compte.',
    },
    scope_global: 'Partout',
    scope_table: 'Table',
    scope_record: 'Enregistrement',
    shortcutCustom: 'personnalisé',
    shortcutNone: 'aucun',
    shortcutAdd: 'Ajouter une combinaison de touches',
    shortcutRemove: 'Retirer {key}',
    shortcutPress: 'Appuyez sur les touches …',
    shortcutRecording: 'Enregistrement : appuyez sur la nouvelle combinaison de touches, Échap annule',
    shortcutReserved: 'Cette combinaison est réservée au navigateur.',
    shortcutPlain: 'La barre d’espace seule ne peut pas être affectée.',
    shortcutTooMany: 'Trop de combinaisons de touches pour cette action.',
    shortcutConflictTitle: 'Combinaison de touches déjà utilisée',
    shortcutConflict: '{key} est déjà utilisée par : {actions}. La retirer là-bas et l’affecter ici ?',
    shortcutReassign: 'Réaffecter',
    shortcutConflicts: 'Combinaisons de touches utilisées plusieurs fois :',
    shortcutResetAll: 'Tout réinitialiser',
    shortcutResetAllConfirm: 'Restaurer tous les raccourcis clavier par défaut ?',
    shortcutResetDone: 'Les raccourcis clavier ont été réinitialisés.',
    oldPassword: 'Mot de passe actuel',
    newPassword: 'Nouveau mot de passe',
    repeatPassword: 'Répéter le nouveau mot de passe',
    changePassword: 'Changer le mot de passe',
    showPasswords: 'Afficher les mots de passe',
    passwordMismatch: 'Les mots de passe ne correspondent pas.',
    passwordChanged: 'Mot de passe modifié avec succès. Les autres sessions ont été fermées.',
    passwordRules: 'Au moins {min} caractères, différent du nom d’utilisateur.',
    passwordSessionsHint: 'Après la modification, vos autres sessions (autres appareils, navigateurs) sont déconnectées.',
    oldPasswordWrong: 'Le mot de passe actuel est incorrect.',
    passwordWeak: 'Le nouveau mot de passe ne respecte pas les règles (au moins {min} caractères, différent du nom d’utilisateur).',
    passwordExternal: 'Le mot de passe de ce compte est géré en externe (p. ex. LDAP) et ne peut pas être modifié ici.',
    pwStrength: 'Robustesse du mot de passe',
    pwScore0: 'Trop faible',
    pwScore1: 'Faible',
    pwScore2: 'Moyen',
    pwScore3: 'Bon',
    pwScore4: 'Très bon',
    pwHint: {
      tooShort: 'au moins {min} caractères',
      username: 'différent du nom d’utilisateur',
      tooLong: 'trop long',
      mix: 'mélanger majuscules, minuscules, chiffres et caractères spéciaux',
      repeat: 'éviter les répétitions et les suites',
      common: 'trop courant',
      longer: 'plus long est plus sûr',
      sameAsOld: 'identique au mot de passe actuel',
    },
    totpEnabled: 'La connexion à deux facteurs est active.',
    totpDisabled: 'La connexion à deux facteurs n’est pas active.',
    totpIntro: 'La connexion demande en plus un code d’une application d’authentification (p. ex. FreeOTP, Google Authenticator, Microsoft Authenticator).',
    totpUnavailable: 'La connexion à deux facteurs n’est pas configurée sur ce serveur.',
    totpStart: 'Configurer',
    totpStep1: 'Scannez le QR-Code avec votre application d’authentification ou saisissez la clé manuellement.',
    totpStep2: 'Saisissez le code affiché par l’application pour confirmer.',
    totpQr: 'QR-Code pour l’application d’authentification',
    totpSecret: 'Clé pour l’application d’authentification',
    totpAccount: 'Compte',
    totpManualHint: 'Basé sur le temps (TOTP), 6 chiffres, 30 secondes.',
    totpLink: 'Ouvrir dans l’application d’authentification',
    totpVerify: 'Confirmer',
    totpCode: 'Code de l’application',
    totpCodeOrBackup: 'Code de l’application ou code de secours',
    totpBackupTitle: 'Codes de secours',
    totpBackup: 'Conservez ces codes en lieu sûr dès maintenant – ils ne sont affichés qu’une seule fois. Chaque code remplace une fois le code de l’application, p. ex. en cas de perte du téléphone.',
    totpBackupSaved: 'J’ai conservé les codes de secours en lieu sûr.',
    copy: 'Copier',
    download: 'Télécharger en fichier texte',
    secretCopied: 'Clé copiée.',
    codesCopied: 'Codes de secours copiés.',
    copyFailed: 'Copie impossible – veuillez sélectionner le texte manuellement.',
    done: 'Terminé',
    totpDisable: 'Désactiver',
    totpDisableHint: 'La désactivation nécessite votre mot de passe et un code actuel (ou un code de secours).',
    totpDisableConfirm: 'Vraiment désactiver la connexion à deux facteurs ? Votre compte ne sera alors protégé que par le mot de passe.',
    totpDisableWrong: 'Mot de passe ou code incorrect.',
    totpDisabledDone: 'La connexion à deux facteurs a été désactivée.',
  },
  error: {
    generic: 'Une erreur est survenue.',
    network: 'Le serveur est injoignable.',
    forbidden: 'Accès refusé.',
    notFound: 'Introuvable.',
    conflict: 'Conflit avec l’état actuel.',
    stale: 'L’enregistrement a été modifié entre-temps par quelqu’un d’autre.',
    validation: 'Veuillez vérifier votre saisie.',
    locked: 'L’enregistrement est en cours de modification.',
    lockedBy: 'L’enregistrement est en cours de modification par {user}.',
    rateLimited: 'Trop de requêtes. Veuillez patienter un instant.',
    server: 'Erreur du serveur. Veuillez réessayer plus tard.',
    pending: 'Cette vue n’est pas encore disponible.',
    pendingCompare: 'La comparaison de deux enregistrements suivra dans une prochaine version.',
    pendingForm: 'Ce formulaire de l’ancien système n’a pas encore été repris.',
    notFoundPage: 'Cette page n’existe pas.',
    toHome: 'Aller à l’accueil',
  },
  shortcuts: {
    title: 'Raccourcis clavier',
    app_search: 'Rechercher / filtrer les tables',
    app_toggleSidebar: 'Afficher/masquer la barre latérale',
    app_help: 'Afficher les raccourcis clavier',
    table_next: 'Ligne suivante',
    table_prev: 'Ligne précédente',
    table_open: 'Ouvrir l’enregistrement sélectionné',
    table_new: 'Créer un nouvel enregistrement',
    table_delete: 'Supprimer l’enregistrement sélectionné',
    table_refresh: 'Actualiser la table',
    record_save: 'Enregistrer',
    record_saveClose: 'Enregistrer et fermer',
    record_close: 'Fermer',
    record_next: 'Enregistrement suivant',
    record_prev: 'Enregistrement précédent',
    record_pageNext: 'Page suivante du formulaire',
    record_pagePrev: 'Page précédente du formulaire',
    browserNote: 'La plupart des navigateurs utilisent Ctrl+Tab et Ctrl+Maj+Tab pour changer d’onglet ; l’application ne les reçoit pas. Pour changer de page du formulaire, utilisez Ctrl+Alt+→ et Ctrl+Alt+←.',
  },
  dev: { elements: 'Éléments de formulaire (développement)' },
};
export default fr;
